import { Model, type ModelProps } from '@/infra/model'
import { computed, observable } from 'mobx'
import { Id } from '@/infra/id'
import { type TimeRange } from '@/infra/time-range'
import { Room } from '@/features/messaging/rooms/Room'

export type ConsultationStatus =
  | 'PendingAvailability'
  | 'PendingBooking'
  | 'PendingCall'
  | 'InProgress'
  | 'Cancelled'
  | 'Incomplete'
  | 'Done'

/**
 * The reason the consultation was not completed.
 */
export type ConsultationIncompleteReason =
  | 'ConsultationNotNeeded'
  | 'ClientMissedCall'
  | 'ProMissedCall'
  | 'PreviousProCompletedCall'
  | 'ConsultationHandledViaChat'
  | 'Other'

/**
 * Why a consultation is closed without being completed.
 */
export interface ConsultationIncompleteOptions {
  reason: ConsultationIncompleteReason
  otherReason?: string
}

/**
 * The step the consultation call is at.
 */
export type ConsultationCallStep =
  'Unknown' | 'Pending' | 'InProgress' | 'Done' | 'Completed'

export type ConsultationProps = ModelProps<Consultation>

export class Consultation extends Model<Consultation> {
  id = Id.none

  @observable accessor jobId = Id.none
  @observable accessor status: ConsultationStatus = 'PendingAvailability'
  @observable accessor times: TimeRange[] = []
  @observable accessor clientUserPublicId = Id.none
  @observable accessor clientDisplayName = ''
  @observable accessor providerUserPublicId = Id.none
  @observable accessor providerDisplayName = ''
  @observable accessor bookedTime: TimeRange | null = null
  @observable accessor requestedLength = 30
  @observable accessor skipScheduling = false
  @observable accessor room = Room.none
  @observable accessor workspaceId = Id.none

  /**
   * When true, indicates that, although the consultation isn't currently booked, it was in the past.
   */
  @observable accessor previouslyBooked = false

  /**
   * Bookable slots of `requestedLength` within the provided times.
   */
  @computed
  get availability() {
    return this.times?.flatMap((a) =>
      a.decomposeRangeInIntervals(this.requestedLength),
    )
  }

  /**
   * Whether the client has yet to book a time.
   */
  @computed
  get isPendingBooking() {
    return this.status === 'PendingBooking'
  }

  /**
   * Whether the provider has yet to provide availability.
   */
  @computed
  get isPendingAvailability() {
    return this.status === 'PendingAvailability'
  }

  /**
   * Whether the consultation can be closed.
   */
  @computed
  get canClose(): boolean {
    return this.skipScheduling || this.callStep === 'Done'
  }

  /**
   * The step the call is at.
   */
  @computed
  get callStep(): ConsultationCallStep {
    if (this.room.roomSource.type === 'Unknown') {
      return 'Pending'
    }

    if (this.room.roomSource.type === 'Local') {
      return 'Done'
    }

    switch (this.status) {
      case 'PendingCall':
        return 'Pending'
      case 'InProgress':
        if (this.hadRoomSession) {
          return 'Done'
        }
        return 'InProgress'
      case 'Done':
        return 'Completed'
      default:
        return 'Unknown'
    }
  }

  /**
   * Whether the call room had a session that ended.
   */
  @computed
  get hadRoomSession(): boolean {
    return this.room.sessions.some((s) => s.status === 'Ended')
  }
}
